"use client";

import { forwardRef, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocale, useTranslations } from "next-intl";
import {
  Building2,
  CheckCircle2,
  Clock,
  Mail,
  MessageSquare,
  Phone,
  Tag,
  User,
} from "lucide-react";
import {
  makeCallbackSchema,
  makeContactSchema,
  type CallbackData,
  type ContactData,
} from "@/lib/forms/schemas";
import { cn } from "@/lib/cn";
import { pushLeadSubmit } from "@/lib/analytics/gtm";
import { splitName } from "@/lib/analytics/hash";

type Mode = "callback" | "message";

export function ContactTabs() {
  const tf = useTranslations("Forms");
  const [mode, setMode] = useState<Mode>("callback");

  return (
    <div className="overflow-hidden rounded-md border border-line-200 bg-canvas">
      <div role="tablist" className="grid grid-cols-2 border-b border-line-200 bg-soft">
        <TabButton
          active={mode === "callback"}
          onClick={() => setMode("callback")}
          icon={<Phone />}
          eyebrow={tf("tabEyebrowFast")}
          label={tf("tabLabelCallback")}
        />
        <TabButton
          active={mode === "message"}
          onClick={() => setMode("message")}
          icon={<MessageSquare />}
          eyebrow={tf("tabEyebrowDetailed")}
          label={tf("tabLabelMessage")}
        />
      </div>

      <div className="p-6 md:p-8 lg:p-10">
        {mode === "callback" ? <CallbackPane /> : <MessagePane />}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  eyebrow,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  eyebrow: string;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "relative flex flex-col items-start gap-1 px-5 py-4 text-left transition-colors duration-150 md:px-6 md:py-5",
        active ? "bg-canvas" : "hover:bg-canvas/60",
      )}
    >
      <span
        className={cn(
          "inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors",
          active ? "text-brand-600" : "text-ink-500",
        )}
      >
        <span className="[&>svg]:h-3.5 [&>svg]:w-3.5">{icon}</span>
        {eyebrow}
      </span>
      <span
        className={cn(
          "font-display text-[15px] leading-snug transition-colors md:text-base",
          active ? "font-semibold text-ink-900" : "font-medium text-ink-700",
        )}
      >
        {label}
      </span>
      {active ? (
        <span
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-0.5 bg-brand-600"
        />
      ) : null}
    </button>
  );
}

/* ───── Callback pane ───── */

function CallbackPane() {
  const locale = useLocale();
  const tf = useTranslations("Forms");
  const tv = useTranslations("Validation");
  const [done, setDone] = useState(false);

  const schema = useMemo(
    () =>
      makeCallbackSchema({
        phoneInvalid: tv("phoneInvalid"),
        phoneChars: tv("phoneChars"),
      }),
    [tv],
  );

  const { register, handleSubmit, formState } = useForm<CallbackData>({
    resolver: zodResolver(schema),
    defaultValues: { phone: "" },
  });

  async function onSubmit(data: CallbackData) {
    let ok = false;
    try {
      const res = await fetch("/api/callback", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ...data, locale }),
      });
      ok = res.ok;
    } finally {
      setDone(true);
      if (ok) await pushLeadSubmit({ form: "callback", userData: { phone: data.phone } });
    }
  }

  if (done) {
    return (
      <p className="inline-flex items-center gap-2 text-sm font-semibold text-success-500">
        <CheckCircle2 className="h-5 w-5" aria-hidden /> {tf("callbackSuccess")}
      </p>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="grid gap-4">
      <p className="text-sm leading-relaxed text-ink-700">{tf("callbackIntro")}</p>
      <IconInput
        icon={<Phone />}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        placeholder={tf("placeholderPhoneShort")}
        className={cn("tabnums", formState.errors.phone && "!border-danger-500")}
        {...register("phone")}
        aria-invalid={!!formState.errors.phone}
      />
      <div className="flex flex-col items-start gap-3 border-t border-line-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
        <p className="inline-flex items-center gap-1.5 text-xs text-ink-500">
          <Clock className="h-3.5 w-3.5" aria-hidden />
          {tf("dispatch247")}
        </p>
        <button type="submit" className="btn-primary btn-lg">
          {tf("requestCallback")}
        </button>
      </div>
    </form>
  );
}

/* ───── Message pane ───── */

function MessagePane() {
  const locale = useLocale();
  const tf = useTranslations("Forms");
  const tv = useTranslations("Validation");
  const [done, setDone] = useState(false);

  const schema = useMemo(
    () =>
      makeContactSchema({
        phoneInvalid: tv("phoneInvalid"),
        phoneChars: tv("phoneChars"),
      }),
    [tv],
  );

  const { register, handleSubmit, formState } = useForm<ContactData>({
    resolver: zodResolver(schema),
    defaultValues: { topic: "quote", name: "", email: "", message: "" },
  });

  async function onSubmit(data: ContactData) {
    let ok = false;
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ ...data, locale }),
      });
      ok = res.ok;
    } finally {
      setDone(true);
      if (ok) {
        const { firstName, lastName } = splitName(data.name);
        await pushLeadSubmit({
          form: "contact",
          topic: data.topic,
          userData: { email: data.email, phone: data.phone, firstName, lastName },
        });
      }
    }
  }

  if (done) {
    return (
      <p className="inline-flex items-center gap-2 text-sm font-semibold text-success-500">
        <CheckCircle2 className="h-5 w-5" aria-hidden /> {tf("contactSuccess")}
      </p>
    );
  }

  const errName = formState.errors.name;
  const errEmail = formState.errors.email;
  const errMessage = formState.errors.message;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="grid gap-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <IconInput
          icon={<User />}
          placeholder={tf("placeholderName")}
          autoComplete="name"
          {...register("name")}
          className={errName ? "!border-danger-500" : undefined}
        />
        <IconInput
          icon={<Building2 />}
          placeholder={tf("placeholderCompany")}
          autoComplete="organization"
          {...register("company")}
        />
        <IconInput
          icon={<Mail />}
          type="email"
          placeholder={tf("placeholderEmail")}
          autoComplete="email"
          {...register("email")}
          className={errEmail ? "!border-danger-500" : undefined}
        />
        <IconInput
          icon={<Phone />}
          type="tel"
          placeholder={tf("placeholderPhone")}
          autoComplete="tel"
          {...register("phone")}
        />
      </div>

      <div className="relative">
        <span
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-500 [&>svg]:h-4 [&>svg]:w-4"
        >
          <Tag />
        </span>
        <select className="input pl-9" {...register("topic")}>
          <option value="quote">{tf("topicQuote")}</option>
          <option value="service">{tf("topicService")}</option>
          <option value="partner">{tf("topicPartner")}</option>
          <option value="press">{tf("topicPress")}</option>
          <option value="other">{tf("topicOther")}</option>
        </select>
      </div>

      <div className="relative">
        <span
          aria-hidden
          className="pointer-events-none absolute left-3 top-3.5 text-ink-500 [&>svg]:h-4 [&>svg]:w-4"
        >
          <MessageSquare />
        </span>
        <textarea
          rows={5}
          placeholder={tf("placeholderMessage")}
          className={cn(
            "input pl-9 py-3 min-h-[120px] resize-none leading-relaxed",
            errMessage && "!border-danger-500",
          )}
          {...register("message")}
        />
      </div>

      {errName || errEmail || errMessage ? (
        <p className="text-xs text-danger-500">{tf("requiredFields")}</p>
      ) : null}

      <div className="mt-2 flex flex-col items-start gap-3 border-t border-line-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-ink-500">{tf("replyWithinDay")}</p>
        <button type="submit" className="btn-primary btn-lg">
          {tf("sendMessage")}
        </button>
      </div>
    </form>
  );
}

/* ───── Shared icon input ───── */

const IconInput = forwardRef<
  HTMLInputElement,
  { icon: React.ReactNode } & React.InputHTMLAttributes<HTMLInputElement>
>(function IconInput({ icon, className, ...rest }, ref) {
  return (
    <div className="relative">
      <span
        aria-hidden
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-500 [&>svg]:h-4 [&>svg]:w-4"
      >
        {icon}
      </span>
      <input ref={ref} {...rest} className={cn("input pl-9", className)} />
    </div>
  );
});
